// web/src/features/home/widgets/FavoritesWidget.tsx
import { useDragReorder } from '@/shared/hooks/useDragReorder';
import { IconStarFilled, IconFlag, IconGrip } from '@/components/ui/Icon';
import { useFavoritesStore } from '@/features/favorites/useFavoritesStore';
import { favoriteHref } from '@/features/favorites/resolveFavoriteContext';
import { useHomeNav } from '../useHomeNav';
import { registerWidget } from '../registry';
import { LIST_ROW } from '../homeStyles';

function FavoritesRender() {
  const nav = useHomeNav();
  const favorites = useFavoritesStore((s) => s.favorites);
  const remove = useFavoritesStore((s) => s.remove);
  const reorder = useFavoritesStore((s) => s.reorder);
  // "Follow up" tracks starred RECORDS only (specific documents); starred pages
  // live in the nav favorites menu instead. Reordering sends just the record
  // ids — the backend updates only those, so the nav's page order is untouched.
  const records = favorites.filter((f) => f.kind === 'record');
  const { handleProps, rowProps } = useDragReorder(records, (next) => reorder(next.map((f) => f.id)));

  if (records.length === 0) {
    return <div className="text-sm text-muted">Star a record to follow up on it here.</div>;
  }
  return (
    <div className="flex flex-col gap-1">
      {records.map((f, i) => {
        const rp = rowProps(i);
        return (
          <div key={f.id} onDragOver={rp.onDragOver} onDrop={rp.onDrop}
            className={`flex items-center gap-2 ${LIST_ROW} ${rp.isOver ? 'ring-2 ring-primary' : ''}`}>
            <span {...handleProps(i)} className="text-muted cursor-grab select-none" title="Drag to reorder"><IconStarFilled size={13} className="text-amber-500" /></span>
            <button className="flex-1 text-left text-[0.8125rem] truncate" onClick={() => nav(favoriteHref(f))} title={f.label}>{f.label}</button>
            <button className="text-muted hover:text-rose-500 text-[0.75rem]" onClick={() => void remove(f.id)} title="Remove">Remove</button>
          </div>
        );
      })}
    </div>
  );
}

// Configure = manage the list itself: drag to rearrange, remove what's done.
function FavoritesConfig() {
  const favorites = useFavoritesStore((s) => s.favorites);
  const remove = useFavoritesStore((s) => s.remove);
  const reorder = useFavoritesStore((s) => s.reorder);
  const records = favorites.filter((f) => f.kind === 'record');
  const { handleProps, rowProps } = useDragReorder(records, (next) => reorder(next.map((f) => f.id)));
  if (records.length === 0) {
    return <div className="text-[0.75rem] text-muted">No starred records yet. Star a record to follow up on it here.</div>;
  }
  return (
    <div className="flex flex-col gap-1">
      <div className="text-meta text-muted">Drag to rearrange; changes apply to the widget immediately.</div>
      <div className="max-h-56 overflow-auto rounded-md border border-border-light">
        {records.map((f, i) => {
          const rp = rowProps(i);
          return (
            <div key={f.id} onDragOver={rp.onDragOver} onDrop={rp.onDrop}
              className={`flex items-center gap-2 px-2 py-1 text-[0.8125rem] ${rp.isOver ? 'ring-2 ring-primary' : ''}`}>
              <span {...handleProps(i)} className="text-muted cursor-grab select-none" title="Drag to reorder"><IconGrip size={13} /></span>
              <span className="flex-1 min-w-0 truncate" title={f.label}>{f.label}</span>
              <button type="button" className="text-muted hover:text-rose-500 text-[0.75rem]" onClick={() => void remove(f.id)}>Remove</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

registerWidget({ type: 'favorites', label: 'Follow up', defaultSize: 'S', icon: <IconFlag size={13} />, accent: '#f43f5e', ConfigForm: FavoritesConfig, Render: FavoritesRender });
